/**
 * Transformation Engine
 *
 * Transforms UI form values to primitive metadata formats.
 * All transformation types supported, all transformations logged.
 *
 * Domain-agnostic: Transformations work for any domain
 * Data-driven: Transformation specs from metadata
 * Life-critical: Every transformation audited
 * Single code path: All values flow through same transformation logic
 */

import Mustache from 'mustache';
import { TransformationSpec } from './types/MetadataTypes';

/**
 * Type guards for TransformationSpec discriminated unions
 */
interface StringTransformSpec extends TransformationSpec {
  type: 'string_transform';
  function_params?: {
    trim?: boolean;
    uppercase?: boolean;
    lowercase?: boolean;
    capitalize?: boolean;
  };
}

interface NumberTransformSpec extends TransformationSpec {
  type: 'number_transform';
  function_params?: {
    round?: number;
    floor?: boolean;
    ceil?: boolean;
  };
}

type TransformSpecWithParams = StringTransformSpec | NumberTransformSpec;

function hasStringTransformParams(spec: TransformationSpec): spec is StringTransformSpec {
  return spec.type === 'string_transform';
}

function hasNumberTransformParams(spec: TransformationSpec): spec is NumberTransformSpec {
  return spec.type === 'number_transform';
}

/**
 * Transformation context
 * All data available to transformations
 */
interface TransformationContext {
  value: any; // The value being transformed
  formData: Record<string, any>; // All form data
  primitives: Map<string, any>; // All primitive instances
  metadata: Record<string, any>; // Template metadata
  timestamp: string; // Current timestamp
  actor: string; // Current user
  // Preserve for future
  tenant?: string;
  session_id?: string;
  correlation_id?: string;
}

/**
 * Transformation result
 * Includes audit information
 */
interface TransformationResult {
  transformed_value: any;
  audit: {
    transformation_type: string;
    original_value: any;
    transformed_value: any;
    timestamp: string;
    context_used: string[]; // Which context fields were used
  };
}

/**
 * Main Transformation Engine
 */
export class TransformationEngine {
  private customTransforms: Map<string, (value: any, context: TransformationContext) => any>;

  constructor() {
    this.customTransforms = new Map();
    this.registerBuiltInTransforms();
  }

  /**
   * Apply transformation to value
   * Single code path: All transformations flow through here
   * Life-critical: Every transformation logged
   */
  transform(
    value: any,
    spec: TransformationSpec | undefined,
    context: TransformationContext,
  ): TransformationResult {
    // No transformation = direct pass-through
    if (!spec || spec.type === 'direct') {
      return {
        transformed_value: value,
        audit: {
          transformation_type: 'direct',
          original_value: value,
          transformed_value: value,
          timestamp: context.timestamp,
          context_used: [],
        },
      };
    }

    let transformed_value: any;
    let context_used: string[] = [];

    // Apply transformation based on type
    switch (spec.type) {
      case 'boolean_to_rule':
        transformed_value = this.transformBooleanToRule(value, spec, context);
        break;

      case 'array_to_json':
        transformed_value = this.transformArrayToJson(value, spec, context);
        break;

      case 'object_merge':
        transformed_value = this.transformObjectMerge(value, spec, context);
        context_used = ['formData']; // Merged with form data
        break;

      case 'template':
        const templateResult = this.transformTemplate(value, spec, context);
        transformed_value = templateResult.value;
        context_used = templateResult.context_used;
        break;

      case 'custom':
        transformed_value = this.transformCustom(value, spec, context);
        context_used = ['custom_function'];
        break;

      case 'conditional':
        transformed_value = this.transformConditional(value, spec, context);
        break;

      case 'aggregate':
        transformed_value = this.transformAggregate(value, spec, context);
        context_used = ['formData', 'primitives'];
        break;

      case 'date_format':
        transformed_value = this.transformDateFormat(value, spec, context);
        break;

      case 'string_transform':
        transformed_value = this.transformString(value, spec, context);
        break;

      case 'number_transform':
        transformed_value = this.transformNumber(value, spec, context);
        break;

      default:
        // Unknown transformation type - fail loudly (life-critical)
        throw new Error(
          `Unknown transformation type: ${spec.type}. ` +
            `All transformation types must be explicitly supported.`,
        );
    }

    return {
      transformed_value,
      audit: {
        transformation_type: spec.type,
        original_value: value,
        transformed_value,
        timestamp: context.timestamp,
        context_used,
      },
    };
  }

  /**
   * Boolean to Policy Rule
   * Checkbox true/false → Policy rule object
   */
  private transformBooleanToRule(
    value: any,
    spec: TransformationSpec,
    context: TransformationContext,
  ): any {
    if (value === true) {
      return (
        spec.true_value || {
          enabled: true,
          validated_at: context.timestamp,
          validated_by: context.actor,
        }
      );
    }

    return spec.false_value || null;
  }

  /**
   * Array to JSON Object
   * Multi-select array → Object with keys
   * Example: ["option1", "option2"] → {option1: true, option2: true}
   */
  private transformArrayToJson(
    value: any,
    spec: TransformationSpec,
    context: TransformationContext,
  ): any {
    if (!Array.isArray(value)) {
      return {};
    }

    return value.reduce(
      (acc, item) => ({
        ...acc,
        [item]: true,
      }),
      {},
    );
  }

  /**
   * Object Merge
   * Merge value with other form data
   */
  private transformObjectMerge(
    value: any,
    spec: TransformationSpec,
    context: TransformationContext,
  ): any {
    return {
      ...context.formData,
      ...value,
      _merged_at: context.timestamp,
    };
  }

  /**
   * Mustache Template Transformation
   * Template string with variable substitution
   * Example: "Patient {{patient_name}} (MRN: {{mrn}})"
   */
  private transformTemplate(
    value: any,
    spec: TransformationSpec,
    context: TransformationContext,
  ): { value: any; context_used: string[] } {
    if (!spec.template) {
      throw new Error('Template transformation requires template string');
    }

    const templateContext = {
      value,
      ...context.formData,
      timestamp: context.timestamp,
      actor: context.actor,
    };

    const rendered = Mustache.render(spec.template, templateContext);

    // Track which variables were used
    const varsUsed = spec.template.match(/{{\s*(\w+)\s*}}/g) || [];
    const context_used = varsUsed.map((v) => v.replace(/[{}]/g, '').trim());

    return {
      value: rendered,
      context_used,
    };
  }

  /**
   * Custom Transformation
   * Calls registered custom function
   */
  private transformCustom(
    value: any,
    spec: TransformationSpec,
    context: TransformationContext,
  ): any {
    if (!spec.function_name) {
      throw new Error('Custom transformation requires function_name');
    }

    const transformFn = this.customTransforms.get(spec.function_name);

    if (!transformFn) {
      throw new Error(
        `Custom transformation function '${spec.function_name}' not registered. ` +
          `Available functions: ${Array.from(this.customTransforms.keys()).join(', ')}`,
      );
    }

    return transformFn(value, context);
  }

  /**
   * Conditional Transformation
   * Apply different transformations based on condition
   */
  private transformConditional(
    value: any,
    spec: TransformationSpec,
    context: TransformationContext,
  ): any {
    if (!spec.condition || !spec.then_transform) {
      throw new Error('Conditional transformation requires condition and then_transform');
    }

    // Evaluate condition (could be JSONLogic or simple expression)
    const conditionMet = this.evaluateCondition(spec.condition, context);

    if (conditionMet && spec.then_transform) {
      return this.transform(value, spec.then_transform, context).transformed_value;
    } else if (spec.else_transform) {
      return this.transform(value, spec.else_transform, context).transformed_value;
    }

    return value;
  }

  /**
   * Aggregate Transformation
   * Combine multiple form fields into one primitive field
   * Example: first_name + last_name → full_name
   */
  private transformAggregate(
    value: any,
    spec: TransformationSpec,
    context: TransformationContext,
  ): any {
    // Aggregate from form data
    const aggregated: Record<string, any> = {};

    Object.entries(context.formData).forEach(([key, val]) => {
      if (val !== null && val !== undefined && val !== '') {
        aggregated[key] = val;
      }
    });

    return aggregated;
  }

  /**
   * Date Format Transformation
   * Convert date to specific format
   */
  private transformDateFormat(
    value: any,
    spec: TransformationSpec,
    context: TransformationContext,
  ): any {
    if (!value) return null;

    const date = new Date(value);

    // Default: ISO 8601
    return date.toISOString();
  }

  /**
   * String Transformation
   * Transform string (uppercase, lowercase, trim, etc.)
   */
  private transformString(
    value: any,
    spec: TransformationSpec,
    context: TransformationContext,
  ): any {
    if (typeof value !== 'string') {
      return String(value);
    }

    let result = value;

    // Apply string operations (from spec.function_params)
    if (hasStringTransformParams(spec)) {
      const params = spec.function_params || {};
      if (params.trim) result = result.trim();
      if (params.uppercase) result = result.toUpperCase();
      if (params.lowercase) result = result.toLowerCase();
      if (params.capitalize) result = result.charAt(0).toUpperCase() + result.slice(1);
    }

    return result;
  }

  /**
   * Number Transformation
   * Transform number (round, floor, ceil, format)
   */
  private transformNumber(
    value: any,
    spec: TransformationSpec,
    context: TransformationContext,
  ): any {
    const num = Number(value);

    if (isNaN(num)) {
      return null;
    }

    if (hasNumberTransformParams(spec)) {
      const params = spec.function_params || {};

      if (params.round !== undefined) {
        return Math.round(num * Math.pow(10, params.round)) / Math.pow(10, params.round);
      }
      if (params.floor) {
        return Math.floor(num);
      }
      if (params.ceil) {
        return Math.ceil(num);
      }
    }

    return num;
  }

  /**
   * Register custom transformation function
   * Allows tenant-specific transformations
   */
  registerCustomTransform(
    name: string,
    fn: (value: any, context: TransformationContext) => any,
  ): void {
    this.customTransforms.set(name, fn);
    console.log('[TransformationEngine] Registered custom transform:', name);
  }

  /**
   * Register built-in transforms
   * Common transformations pre-registered
   */
  private registerBuiltInTransforms(): void {
    // SSN Masking (HIPAA compliance)
    this.registerCustomTransform('ssn_mask', (value) => {
      if (!value) return null;
      const ssn = String(value).replace(/\D/g, '');
      if (ssn.length !== 9) return value;
      return `XXX-XX-${ssn.slice(-4)}`;
    });

    // Phone formatting
    this.registerCustomTransform('phone_format', (value) => {
      if (!value) return null;
      const phone = String(value).replace(/\D/g, '');
      if (phone.length === 10) {
        return `(${phone.slice(0, 3)}) ${phone.slice(3, 6)}-${phone.slice(6)}`;
      }
      return value;
    });

    // MRN generation
    this.registerCustomTransform('generate_mrn', (value, context) => {
      // Generate medical record number
      const timestamp = Date.now().toString(36).toUpperCase();
      const random = Math.random().toString(36).substr(2, 4).toUpperCase();
      return `MRN${timestamp}${random}`;
    });

    // Currency formatting
    this.registerCustomTransform('currency_format', (value) => {
      if (value === null || value === undefined) return null;
      const num = Number(value);
      if (isNaN(num)) return value;
      return `$${num.toFixed(2)}`;
    });
  }

  /**
   * Evaluate condition (simple or JSONLogic)
   */
  private evaluateCondition(condition: any, context: TransformationContext): boolean {
    // Simple string expressions
    if (typeof condition === 'string') {
      // Simple comparisons like "field === 'value'"
      // For now, return true (JSONLogic will handle complex)
      return true;
    }

    // JSONLogic (will be integrated in next iteration)
    // const jsonLogic = require('json-logic-js');
    // return jsonLogic.apply(condition, context.formData);

    return true;
  }
}
